import { useState, FormEvent } from 'react';
import { useAuth } from '../contexts/AuthContext';
import './Login.css';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.allgrassroots.invalid';

function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const { forcePasswordChange } = await login(email, password);
      // Redirect invited admins to Settings so they can set a permanent password
      if (forcePasswordChange) {
        window.location.href = '/settings?changePassword=1';
      }
      // Otherwise AuthContext update triggers the authenticated app to render automatically
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setForgotError('');
    setForgotLoading(true);

    try {
      const res = await fetch(`${API_URL}/api/v1/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Something went wrong');
      }

      setForgotSent(true);
    } catch (err) {
      setForgotError(err instanceof Error ? err.message : 'Failed to send reset email');
    } finally {
      setForgotLoading(false);
    }
  };

  if (showForgot) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-header">
            <img src="/logo.png" alt="allgrassroots.com" className="login-logo" />
            <p className="login-subtitle">Reset your password</p>
          </div>

          {forgotSent ? (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 40, marginBottom: 16 }}>✉️</div>
              <p style={{ color: 'var(--text-primary)', fontSize: 15, lineHeight: 1.6, marginBottom: 24 }}>
                If <strong>{forgotEmail}</strong> is registered, you'll receive a password reset link shortly. Check your inbox (and spam folder).
              </p>
              <button
                className="btn btn-ghost"
                onClick={() => {
                  setShowForgot(false);
                  setForgotSent(false);
                  setForgotEmail('');
                }}
                style={{ width: '100%' }}
              >
                ← Back to login
              </button>
            </div>
          ) : (
            <form onSubmit={handleForgotSubmit} className="login-form">
              {forgotError && <div className="error-message">{forgotError}</div>}

              <p style={{ color: 'var(--text-secondary)', fontSize: 14, lineHeight: 1.6, marginBottom: 20 }}>
                Enter your admin email and we'll send you a link to reset your password.
              </p>

              <div className="form-group">
                <label htmlFor="forgot-email" className="form-label">
                  Email
                </label>
                <input
                  id="forgot-email"
                  type="email"
                  className="form-input"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  required
                  autoComplete="email"
                  placeholder="you@yourclub.com"
                  autoFocus
                />
              </div>

              <button
                type="submit"
                className="btn btn-primary login-btn"
                disabled={forgotLoading}
              >
                {forgotLoading ? 'Sending...' : 'Send Reset Link'}
              </button>

              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => { setShowForgot(false); setForgotError(''); }}
                style={{ width: '100%', marginTop: 8 }}
              >
                ← Back to login
              </button>
            </form>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <img src="/logo.png" alt="allgrassroots.com" className="login-logo" />
          <p className="login-subtitle">Admin Dashboard</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {error && <div className="error-message">{error}</div>}

          <div className="form-group">
            <label htmlFor="email" className="form-label">
              Email
            </label>
            <input
              id="email"
              type="email"
              className="form-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              placeholder="you@yourclub.com"
            />
          </div>

          <div className="form-group">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <label htmlFor="password" className="form-label">
                Password
              </label>
              <button
                type="button"
                onClick={() => { setShowForgot(true); setForgotEmail(email); }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--primary-green)',
                  fontSize: 13,
                  cursor: 'pointer',
                  padding: 0,
                  fontFamily: 'inherit',
                }}
              >
                Forgot password?
              </button>
            </div>
            <input
              id="password"
              type="password"
              className="form-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              placeholder="Enter your password"
            />
          </div>

          <button
            type="submit"
            className="btn btn-primary login-btn"
            disabled={isLoading}
          >
            {isLoading ? 'Logging in...' : 'Login'}
          </button>
        </form>

        <div className="login-footer">
          <p className="login-note">
            This dashboard is for authorized club administrators only.
          </p>
        </div>
      </div>
    </div>
  );
}

export default Login;
